// Top-bar control to pick the active Company. Single-company tenants see a static label;
// multi-company tenants see a dropdown; zero-company tenants see a "Create your first company" CTA
// that opens an inline drawer.

import { useState } from 'react';
import { useCompanies, useCreateCompany, useDeleteCompany, useSwitchCompany, type CompanyCreateInput, type CompanyRow } from '@/api/admin';
import { useAuthStore } from '@/store/auth';
import { FormDrawer } from '@/components/FormDrawer';
import { Input } from '@/components/primitives/Input';
import { PasswordConfirmModal } from '@/components/primitives/PasswordConfirmModal';
import { fieldErrorMessage } from '@/lib/api';
import { isValidGstin } from '@/utils/gstin';
import { cn } from '@/lib/cn';

export function CompanySwitcher() {
  const companies = useCompanies();
  const switchCompany = useSwitchCompany();
  const deleteCompany = useDeleteCompany();
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<CompanyRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const rows = companies.data?.rows ?? [];
  const active = rows.find((c) => c.id === activeCompanyId) ?? null;

  async function confirmDelete(password: string) {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await deleteCompany.mutateAsync({ id: toDelete.id, password });
      setToDelete(null);
    } catch (e) {
      setDeleteError(fieldErrorMessage(e, 'Could not delete this company.'));
    }
  }

  if (companies.isLoading) {
    return (
      <span className="text-caption text-cool">Loading…</span>
    );
  }

  if (rows.length === 0) {
    return (
      <>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border border-citrus/40 bg-citrus/[0.05] px-3 py-1.5 text-body-sm text-obsidian hover:bg-citrus/10"
        >
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-citrus text-snow text-[11px] leading-none font-medium">+</span>
          Create your first company
        </button>
        <CompanyCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} />
      </>
    );
  }

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border bg-frost px-3 py-1.5 text-body-sm transition',
            active
              ? 'border-mist text-obsidian hover:border-cobalt'
              : 'border-citrus/40 bg-citrus/[0.05] text-obsidian hover:bg-citrus/10',
          )}
          aria-haspopup="listbox"
          aria-expanded={pickerOpen}
        >
          <span className="inline-block h-2 w-2 rounded-full bg-cobalt" aria-hidden />
          <span className="font-medium">{active?.name ?? 'Pick a company'}</span>
          <span className="text-cool" aria-hidden>▾</span>
        </button>

        {pickerOpen && (
          <div className="absolute right-0 z-30 mt-1 min-w-[220px] rounded-md border border-mist bg-snow p-1 shadow-card">
            {rows.map((c) => {
              const isActive = c.id === activeCompanyId;
              return (
                <div
                  key={c.id}
                  className={cn(
                    'group flex items-center gap-1 rounded-sm',
                    isActive ? 'bg-cobalt/[0.05]' : 'hover:bg-haze',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setPickerOpen(false);
                      switchCompany.mutate(c.id);
                    }}
                    className={cn(
                      'flex min-w-0 flex-1 items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-body-sm',
                      isActive ? 'text-cobalt' : 'text-obsidian',
                    )}
                  >
                    <span className="truncate">{c.name}</span>
                    {isActive && <span className="text-caption text-cobalt">●</span>}
                  </button>
                  {/* Delete: only for non-active companies (you can't retire the one you're in). */}
                  {!isActive && (
                    <button
                      type="button"
                      title={`Delete ${c.name}`}
                      aria-label={`Delete ${c.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickerOpen(false);
                        setDeleteError(null);
                        setToDelete(c);
                      }}
                      className="mr-1 grid h-7 w-7 shrink-0 place-items-center rounded-sm text-cool opacity-0 transition hover:bg-crimson/10 hover:text-crimson focus:opacity-100 group-hover:opacity-100"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              );
            })}
            <div className="my-1 h-px bg-mist" />
            <button
              type="button"
              onClick={() => {
                setPickerOpen(false);
                setCreateOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-body-sm text-cobalt hover:bg-cobalt/[0.05]"
            >
              <span className="text-lg leading-none">+</span> New company
            </button>
          </div>
        )}
      </div>

      <CompanyCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} />

      <PasswordConfirmModal
        open={toDelete !== null}
        title="Delete this company?"
        body={toDelete ? `"${toDelete.name}" will be archived and removed from your company list. Its books and audit trail are kept on file (nothing is erased), but you won't be able to use or switch into it.` : undefined}
        warning={toDelete ? `Deleting "${toDelete.name}"` : undefined}
        confirmLabel="Delete company"
        tone="danger"
        busy={deleteCompany.isPending}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setToDelete(null); setDeleteError(null); }}
      />
    </>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function CompanyCreateDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateCompany();
  const switchCompany = useSwitchCompany();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    legalName: '',
    pan: '',
    gstin: '',
    contactEmail: '',
    contactPhone: '',
    line1: '',
    city: '',
    state: 'Maharashtra',
    stateCode: '27',
    pincode: '',
  });

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setError(null);
    const payload: CompanyCreateInput = {
      name: form.name.trim(),
      legalName: form.legalName.trim() || undefined,
      pan: form.pan.trim().toUpperCase() || undefined,
      gstin: form.gstin.trim().toUpperCase() || undefined,
      contactEmail: form.contactEmail.trim() || undefined,
      contactPhone: form.contactPhone.trim() || undefined,
    };

    // Address is optional and all-or-nothing. If the user started filling it in,
    // catch a partial entry HERE with a plain message — the backend requires
    // line1 + city + state + stateCode + pincode together and would otherwise
    // reject the whole request with a generic "Validation failed".
    const anyAddr = !!(form.line1.trim() || form.city.trim() || form.pincode.trim());
    if (anyAddr) {
      const missing: string[] = [];
      if (!form.line1.trim()) missing.push('address line 1');
      if (!form.city.trim()) missing.push('city');
      if (!form.state.trim()) missing.push('state');
      if (!/^(0[1-9]|[12][0-9]|3[0-7])$/.test(form.stateCode.trim())) missing.push('a valid 2-digit state code (01–37)');
      if (!/^[1-9]\d{5}$/.test(form.pincode.trim())) missing.push('a 6-digit PIN code');
      if (missing.length > 0) {
        setError(`Registered address is incomplete — it needs ${missing.join(', ')}. Fill it in, or clear all address fields to skip it.`);
        return;
      }
      payload.address = {
        line1: form.line1.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        stateCode: form.stateCode.trim(),
        pincode: form.pincode.trim(),
      };
    }

    try {
      const company = await create.mutateAsync(payload);
      // Auto-switch to the newly created company so the user can immediately use Masters.
      await switchCompany.mutateAsync(company.id);
      onClose();
    } catch (e) {
      // Expand the backend's per-field reasons instead of a dead-end "Validation failed".
      setError(fieldErrorMessage(e, 'Unable to create company.'));
    }
  }

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      onSubmit={save}
      size="xl"
      title="New Company"
      subtitle="A company is one legal entity in your workspace — its own books, branches, vouchers and ledgers. You can run many side-by-side under the same tenant."
      saving={create.isPending || switchCompany.isPending}
      saveDisabled={!form.name.trim()}
      error={error}
      dirty={
        !!form.name ||
        !!form.legalName ||
        !!form.pan ||
        !!form.contactEmail ||
        !!form.contactPhone ||
        !!form.line1 ||
        !!form.city ||
        !!form.pincode
      }
    >
      <section>
        <div className="mb-3 flex items-center gap-3">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-cobalt text-snow text-caption font-medium">1</span>
          <h3 className="font-display text-display-sm font-semibold text-obsidian">Identity</h3>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Input label="Company name" required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Raaj Industries 2026-27" />
          <Input label="Legal name (optional)" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} placeholder="As on PAN / GST registration" />
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Input
            label="GSTIN (optional)"
            value={form.gstin}
            onChange={(e) => set('gstin', e.target.value.toUpperCase())}
            maxLength={15}
            className="font-mono"
            placeholder="27AAACA1234M1Z5"
            hint="We'll set up your Head Office branch with this GSTIN"
            error={form.gstin.trim() && !isValidGstin(form.gstin)
              ? 'Doesn\'t look like a valid GSTIN — check for typos (the last character is a checksum)'
              : undefined}
          />
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-3">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-cobalt text-snow text-caption font-medium">2</span>
          <h3 className="font-display text-display-sm font-semibold text-obsidian">Contact</h3>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <Input label="PAN (optional)" value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} maxLength={10} className="font-mono" placeholder="ABCDE1234F" />
          <Input label="Contact phone" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} maxLength={20} placeholder="+91 98765 43210" />
          <Input label="Contact email" type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="ledgers@company.com" />
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-3">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-cobalt text-snow text-caption font-medium">3</span>
          <h3 className="font-display text-display-sm font-semibold text-obsidian">Registered address <span className="text-caption text-cool font-normal">(optional)</span></h3>
        </div>
        <div className="rounded-md border border-mist bg-haze/70 p-4">
          <div className="flex flex-col gap-4">
            <Input label="Address line 1" value={form.line1} onChange={(e) => set('line1', e.target.value)} placeholder="Plot no · street · area" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input label="City" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Nagpur" />
              <Input label="State" value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="Maharashtra" />
              <Input label="State code" value={form.stateCode} onChange={(e) => set('stateCode', e.target.value)} maxLength={2} className="font-mono" placeholder="27" />
            </div>
            <Input label="PIN code" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} maxLength={6} className="font-mono" placeholder="440001" />
          </div>
        </div>
      </section>

      <div className="rounded-md border border-cobalt/40 bg-cobalt/[0.05] px-4 py-3 text-body-sm text-obsidian">
        <span className="font-medium text-cobalt">After save:</span> we&apos;ll auto-switch you into this company so you can add Parties, Items and Ledgers right away. The chart of accounts and voucher-type series seed automatically.
      </div>
    </FormDrawer>
  );
}

